import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { EditOutlined as EditOutlinedIcon } from '@mui/icons-material'

import type { RecordingViewModel } from '@/apps/latest/shared/recording-status/types'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { DURATION } from '@/shared/styles/motion'
import { RADIUS } from '@/shared/styles/radii'
import {
  FONT_WEIGHT,
  ICON_SIZE,
  scaledFontSize,
} from '@/shared/styles/typography'
import type { RecordingPreset } from '@/features/yakureki/recording/streaming/recordingPreset'
import { useAnyCompassEmbed } from '@/features/anycompass'
import {
  type PatientGender,
  type SessionType,
  type YakurekiDetail,
} from '@/shared/types/yakurekiCore'

import {
  RecordingCtaCluster,
  recordingStatusCaption,
} from '../RecordingCtaCluster'
import { RecordingContextMenu } from '../RecordingContextMenu'
import { RecordingModeMenu } from '../RecordingModeMenu'
import { PharmacistMenu } from '../PharmacistMenu'
import { type Pharmacist } from '@/features/auth/types/pharmacy'

const genderLabel = (g?: PatientGender): string => {
  if (g === 'male') return '男性'
  if (g === 'female') return '女性'
  if (g === 'other') return 'その他'
  return ''
}

interface Props {
  event: YakurekiDetail
  // page で組み立てた L3 view-model. CTA / status caption の駆動.
  // 渡さない場合 (= Post 画面など) は録音 CTA / caption ともに非表示.
  recordingStatus?: RecordingViewModel
  // 録音 CTA click. recordingStatus が渡される場合のみ意味を持つ.
  onStartRecording?: () => void
  // 録音開始前の同伴/代理人有 toggle. recordingStatus.status==='idle' のときだけ
  // ⫻ trigger を露出. 省略時は popover を表示しない (= Post 画面など).
  hasCompanion?: boolean
  onCompanionToggle?: (value: boolean) => void
  // 来局/在宅 (session_type). mic 左の pill 列の先頭で選ぶ. 省略時は session pill 非表示.
  // 在宅を選ぶと録音完了後に訪問報告書が生成される. 変更は録音前 idle のときのみ.
  sessionType?: SessionType
  onSessionTypeChange?: (sessionType: SessionType) => void
  // 録音モード (標準/深掘り). mic 左の pill で選ぶ. 省略時は mode pill 非表示.
  preset?: RecordingPreset
  onPresetChange?: (preset: RecordingPreset) => void
  // 担当薬剤師の選択. mode pill の隣に並べる. 薬剤師が 2 名以上で全て揃う時のみ表示.
  pharmacists?: Pharmacist[]
  sessionPharmacistId?: string
  onPharmacistChange?: (id: string) => void
  // 録音せずに SOAP へ進む導線. AnyCOMPASS 埋め込み時の録音前 idle のみ露出.
  // 省略時は導線を出さない.
  //
  // 押した結果 (空 SOAP を起こすだけ / メモから生成する) は caller が決める. ここは
  // 「何が起きるか」を label と tooltip で受け取るだけで、 分岐の意味を持たない.
  onSkipRecording?: () => void
  isSkippingRecording?: boolean
  skipRecordingLabel?: string
  skipRecordingTooltip?: string
}

// 服薬指導の最低限の患者情報を main 上部に表示する.
// nasu VisitPage.tsx:262-296 の patient header (28 spec 5 原則 1: 罫線排し白抜き) を踏襲し、
// 背景 / 罫線 / 色 chip は使わない. 余白と typography だけで構成 (= 完全 flat).
//
// §A-4「ヘッダは 1 行で完結」: 主役 = 患者名 (大) + 年齢・性別・担当薬剤師 (小,「46歳 男性 担当 ○○」)
// を 1 行に同居. 担当薬剤師は監査上どこかに残す必要があるため、 同じ 1 行に caption として表示する.
// 日付は非表示.
// 右側は録音の終端操作群 (RecordingCtaCluster. ADR-0040): idle の pill 群だけこの部品が組み、
// 破棄 / 一時停止 / 停止 / paused の 3 終端は cluster が持つ (まとめて録音と同じ部品).
// 二次アクション (加算履歴 等) は本バーに置かず、 ページ下部の PatientActionMenu 行へ分離.
export const PatientInfoBar = ({
  event,
  recordingStatus,
  onStartRecording,
  hasCompanion,
  onCompanionToggle,
  sessionType,
  onSessionTypeChange,
  preset,
  onPresetChange,
  pharmacists,
  sessionPharmacistId,
  onPharmacistChange,
  onSkipRecording,
  isSkippingRecording = false,
  skipRecordingLabel = '録音せずに記載',
  skipRecordingTooltip = '録音せずに SOAP を記載する',
}: Props) => {
  // AnyCOMPASS 埋め込み時、患者名・年齢・性別はホストの患者バンドに既出のため出さない
  // (二重表示の引き算)。担当薬剤師はホスト側に無い監査表示なので埋め込みでも残す。
  const { isEmbedded } = useAnyCompassEmbed()

  // 1 行目に同居させる年齢 + 性別 (=「46歳 男性」を同じ行に). 片方欠ける場合は在る方のみ.
  const demographic = [
    event.age != null ? `${event.age}歳` : '',
    genderLabel(event.gender),
  ]
    .filter(Boolean)
    .join(' ')

  // viewStatus 由来の status caption (= 生成中 / 完了 / 失敗 / paused 系) は名前行の脇に出す.
  const sc = recordingStatus ? recordingStatusCaption(recordingStatus) : null

  const displayName = event.patient_name || event.label || '(無名)'

  return (
    // 狭幅では操作列を次の段へ送る. 操作列は flexShrink:0 なので、 折返しを許さないと
    // 文字拡大「大」で右へはみ出して録音 CTA が切れる (埋め込み 34% = 464px 実測で 12px).
    // 折返しを許すと header は 140 → 87px に縮み、 はみ出しも消える.
    // 幅が足りている間 (単体配信・埋め込み 50% 展開) は 1 行のままで見た目は変わらない.
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        // shorthand の gap は使わない. responsive な gap は media query として出るので、
        // 後ろに rowGap を書いても打ち消せない (実測で 8px 指定が 16px のまま残った).
        columnGap: { xs: 1, sm: 2 },
        rowGap: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* 1 行目: 患者名 (大) + 年齢・性別・担当 (小) + status caption + 同伴 chip. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 0.75,
          }}
        >
          {!isEmbedded && (
            <Typography
              variant="h6"
              component="h1"
              sx={{ fontWeight: 600, lineHeight: 1.3 }}
            >
              {displayName}
            </Typography>
          )}
          {!isEmbedded && demographic && (
            <Typography variant="caption" color="text.secondary">
              {demographic}
            </Typography>
          )}
          {event.pharmacist_name && (
            <Typography variant="caption" color="text.secondary">
              担当 {event.pharmacist_name}
            </Typography>
          )}
          {sc && (
            <Typography variant="caption" color="text.secondary">
              {sc}
            </Typography>
          )}
          {/* 在宅のみ控えめに示す (= 来局は既定なので出さない / 引き算). idle 以外 (録音中/結果)
              で表示し、報告書タブが出る根拠を静かに補足する. 色もアイコンも足さず、淡色の文字
              だけの modest な表示にする (28 spec: 重要度を色で出さない). */}
          {event.session_type === 'home_visit' &&
            recordingStatus?.status !== 'idle' && (
              <Typography
                variant="caption"
                sx={{ color: colorTokens.fg.muted }}
              >
                在宅
              </Typography>
            )}
          {/* 同伴/代理人有 chip — idle 以外で表示. idle の間は popover で toggle 中. */}
          {event.has_companion_or_substitute &&
            recordingStatus?.status !== 'idle' && (
              <Chip
                label="同伴/代理人有"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 11,
                  bgcolor: colorTokens.state.infoBg,
                  color: colorTokens.state.infoFg,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
        </Box>
      </Box>
      {recordingStatus && (
        <RecordingCtaCluster
          recordingStatus={recordingStatus}
          onStartRecording={() => onStartRecording?.()}
          idleControls={
            <>
              {/* idle 時のみ録音前の選択 pill を mic 左に並べる (Gemini 流):
                  録音モード (標準/深掘り) → 録音の文脈 (来局/在宅 + 本人のみ/同伴・代理あり).
                  来局/在宅は独立 pill にせず文脈メニューに畳む (= pill を増やさない).
                  録音モードは batch 経路では出さない (log/207): legacy batch upload に preset の
                  受け口が無く、選ばせても届かないため. */}
              {recordingStatus.transcriptionRoute !== 'batch' &&
                preset &&
                onPresetChange && (
                  <RecordingModeMenu value={preset} onChange={onPresetChange} />
                )}
              {pharmacists &&
                pharmacists.length >= 2 &&
                sessionPharmacistId !== undefined &&
                onPharmacistChange && (
                  <PharmacistMenu
                    pharmacists={pharmacists}
                    value={sessionPharmacistId}
                    onChange={onPharmacistChange}
                  />
                )}
              {hasCompanion !== undefined && onCompanionToggle && (
                <RecordingContextMenu
                  sessionType={sessionType}
                  onSessionTypeChange={onSessionTypeChange}
                  hasCompanion={hasCompanion}
                  onCompanionChange={onCompanionToggle}
                />
              )}
              {/* 録音せずに SOAP へ進む (Zendesk #2237). 席投薬など「録音するほどでもない」場面で
                  生成待ちゼロ / メモだけで SOAP へ入る導線. 主役の録音 CTA は奪わず、pill 列の末尾に
                  低 emphasis で置く (= mic の対抗馬にしない).
                  AnyCOMPASS 埋め込み中のみ (= 連携ビルドにしか出ない).
                  メモの有無で振る舞いが変わるが、 それは label が宣言する (= 同じ場所・同じ icon で、
                  押した結果だけが文言どおりに変わる). */}
              {onSkipRecording && (
                <Tooltip title={skipRecordingTooltip} placement="bottom">
                  {/* disabled な button を Tooltip 直下に置くと、 押下中だけ tooltip が
                      黙って効かなくなる (無効要素はイベントを出さない). 一時停止・転記・
                      コピーと同じく span で包んで受け手を作る. */}
                  <span>
                    <Box
                      component="button"
                      type="button"
                      aria-label={skipRecordingLabel}
                      disabled={isSkippingRecording}
                      onClick={onSkipRecording}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        minHeight: 32,
                        px: 1.25,
                        border: 'none',
                        borderRadius: `${RADIUS.full}px`,
                        fontFamily: 'inherit',
                        fontSize: scaledFontSize('label'),
                        fontWeight: FONT_WEIGHT.medium,
                        color: colorTokens.fg.muted,
                        bgcolor: 'transparent',
                        cursor: isSkippingRecording ? 'default' : 'pointer',
                        opacity: isSkippingRecording ? 0.5 : 1,
                        transition: `background-color ${DURATION.fast}ms, color ${DURATION.fast}ms`,
                        '&:hover': {
                          bgcolor: isSkippingRecording
                            ? 'transparent'
                            : colorTokens.overlay.hover,
                          color: colorTokens.fg.default,
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          '& svg': { fontSize: ICON_SIZE.md, display: 'block' },
                        }}
                      >
                        <EditOutlinedIcon />
                      </Box>
                      {skipRecordingLabel}
                    </Box>
                  </span>
                </Tooltip>
              )}
            </>
          }
        />
      )}
    </Box>
  )
}
